// src/Product/Onboarding.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./onboarding.css";
import {
  Building2,
  User2,
  ShieldCheck,
  FileText,
  BadgeCheck,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

const INDUSTRIES = [
  "Technology",
  "Finance",
  "Healthcare",
  "Retail",
  "Manufacturing",
  "Education",
  "Real Estate",
  "Transportation",
  "Energy",
  "Agriculture",
  "Hospitality",
  "Media & Entertainment",
  "Telecommunications",
  "Professional Services",
  "Construction",
];

const BUSINESS_TYPES = [
  "Sole Proprietorship",
  "Partnership",
  "Limited Liability Company (Ltd)",
  "Public Limited Company (Plc)",
  "Incorporated Trustees (NGO)",
  "Other",
];

const ID_TYPES = [
  "NIN",
  "International Passport",
  "Driver's License",
  "Voter's Card",
];
const PAYMENT_METHODS = ["Transfer", "Cash", "POS", "Card", "USSD"];

const FEATURE_OPTIONS = [
  "Invoicing",
  "Receipts",
  "Estimates",
  "Customers",
  "Bank Transactions",
  "Journal Entries",
  "General Ledger",
  "Trial Balance",
  "Income Statement",
  "Balance Sheet",
  "Cashflows",
  "Reconciliation",
  "Management Report",
];

const monthISO = () => new Date().toISOString().split("T")[0];

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
}

function compact(payload) {
  const out = {};
  Object.entries(payload || {}).forEach(([k, v]) => {
    if (v === "" || v === null || typeof v === "undefined") return;
    if (Array.isArray(v) && v.length === 0) return;
    out[k] = v;
  });
  return out;
}

const WALKTHROUGH_TABS = [
  {
    key: "Getting Started",
    icon: <BadgeCheck size={16} />,
    videos: [
      {
        title: "Welcome to KostPadi",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
      {
        title: "How to navigate the dashboard",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
    ],
  },
  {
    key: "Invoicing",
    icon: <FileText size={16} />,
    videos: [
      {
        title: "Create an invoice",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
      {
        title: "Print / Save as PDF",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
    ],
  },
  {
    key: "Accounting",
    icon: <ShieldCheck size={16} />,
    videos: [
      {
        title: "Journal entries (how postings work)",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
      {
        title: "Reports (TB, GL, IS, SOFP)",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
    ],
  },
  {
    key: "Banking",
    icon: <Building2 size={16} />,
    videos: [
      {
        title: "Import / manage bank transactions",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
      {
        title: "Reconciliation walkthrough",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
    ],
  },
  {
    key: "Billing & Team",
    icon: <User2 size={16} />,
    videos: [
      {
        title: "Billing plans + upgrades",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
      {
        title: "Team settings + roles",
        url: "https://www.youtube.com/watch?v=6cKDKi7mACQ",
      },
    ],
  },
];

function WalkthroughModal({ open, onClose }) {
  const [tab, setTab] = useState(WALKTHROUGH_TABS[0].key);

  useEffect(() => {
    if (open) setTab(WALKTHROUGH_TABS[0].key);
  }, [open]);

  if (!open) return null;

  const active =
    WALKTHROUGH_TABS.find((t) => t.key === tab) || WALKTHROUGH_TABS[0];

  return (
    <div className="kp-modal-overlay" role="dialog" aria-modal="true">
      <div className="kp-modal">
        <div className="kp-modal-head">
          <div>
            <h3>KostPadi Walkthrough</h3>
            <p className="kp-muted">
              Short videos to help you master every feature. You can reopen this
              anytime via the Help button.
            </p>
          </div>
          <button className="kp-icon-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="kp-modal-body">
          <aside className="kp-tabs">
            {WALKTHROUGH_TABS.map((t) => (
              <button
                key={t.key}
                className={`kp-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
                type="button"
              >
                <span className="kp-tab-ic">{t.icon}</span>
                <span>{t.key}</span>
              </button>
            ))}
          </aside>

          <section className="kp-videos">
            <div className="kp-videos-head">
              <h4>{active.key}</h4>
              <span className="kp-pill">{active.videos.length} video(s)</span>
            </div>

            <div className="kp-video-grid">
              {active.videos.map((v, i) => (
                <div className="kp-video-card" key={`${active.key}-${i}`}>
                  <div className="kp-video-frame">
                    <iframe
                      src={v.url}
                      title={v.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                  <div className="kp-video-title">{v.title}</div>
                </div>
              ))}
            </div>

            <div className="kp-hint">
              Tip: If you don’t see video thumbnails in your PDF export, ensure
              “Background graphics” is enabled and margins are set to “None”.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

async function getCompanyOnboarding(companyId) {
  if (!companyId) return { completed: false, row: null, error: null };
  const { data, error } = await supabase
    .from("company_onboarding")
    .select("company_id, completed, completed_at, completed_by")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) return { completed: false, row: null, error };
  return { completed: !!data?.completed, row: data || null, error: null };
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { companyId } = useCompany();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageErr, setPageErr] = useState("");

  const [user, setUser] = useState(null);

  // ✅ FIX: proper profile state + setter
  const [, setProfile] = useState(null);

  const [helpOpen, setHelpOpen] = useState(false);

  const [step, setStep] = useState(0);
  const [accountType, setAccountType] = useState("");

  const [contact, setContact] = useState({
    email: "",
    phone: "",
    country: "Nigeria",
    state: "",
    address: "",
  });

  const [preferences, setPreferences] = useState({
    currency: "NGN",
    invoicePrefix: "INV-",
    paymentMethods: ["Transfer"],
    features: ["Invoicing", "Journal Entries", "Management Report"],
    taxSystem: "Nigeria",
    vatRegistered: false,
    vatRate: 0.075,
  });

  const [individual, setIndividual] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    idType: "NIN",
    idNumber: "",
    bvn: "",
    nin: "",
    nextOfKinName: "",
    nextOfKinPhone: "",
  });

  const [business, setBusiness] = useState({
    legalName: "",
    tradingName: "",
    industry: "",
    businessType: "Limited Liability Company (Ltd)",
    rcNumber: "",
    tin: "",
    incorporationDate: "",
    website: "",
    directorName: "",
    directorIdType: "NIN",
    directorIdNumber: "",
  });

  const [consent, setConsent] = useState({
    privacy: false,
    kyc: false,
    truthful: false,
  });

  const steps = useMemo(() => {
    const base = [
      { key: "welcome", title: "Welcome", icon: <BadgeCheck size={16} /> },
      { key: "type", title: "Account Type", icon: <User2 size={16} /> },
      { key: "contact", title: "Contact", icon: <FileText size={16} /> },
    ];

    const kyc =
      accountType === "business"
        ? [
            {
              key: "business_kyc",
              title: "Business KYC",
              icon: <Building2 size={16} />,
            },
          ]
        : accountType === "individual"
          ? [
              {
                key: "individual_kyc",
                title: "Personal KYC",
                icon: <User2 size={16} />,
              },
            ]
          : [];

    const rest = [
      { key: "prefs", title: "Preferences", icon: <ShieldCheck size={16} /> },
      { key: "review", title: "Review", icon: <BadgeCheck size={16} /> },
    ];

    return [...base, ...kyc, ...rest];
  }, [accountType]);

  const current = steps[step]?.key;

  const canGoBack = step > 0;
  const canGoNext = step < steps.length - 1;

  const setFeatureToggle = (v) => {
    setPreferences((p) => ({
      ...p,
      features: p.features.includes(v)
        ? p.features.filter((x) => x !== v)
        : [...p.features, v],
    }));
  };

  const setPaymentToggle = (v) => {
    setPreferences((p) => ({
      ...p,
      paymentMethods: p.paymentMethods.includes(v)
        ? p.paymentMethods.filter((x) => x !== v)
        : [...p.paymentMethods, v],
    }));
  };

  const validateStep = useCallback(() => {
    setPageErr("");

    if (current === "type") {
      if (!accountType)
        return "Please select an account type (Individual or Business).";
    }

    if (current === "contact") {
      if (!contact.email || !isEmail(contact.email))
        return "Please enter a valid email address.";
      if (!contact.phone || String(contact.phone).length < 7)
        return "Please enter a valid phone number.";
      if (!contact.country) return "Country is required.";
      if (!contact.address) return "Address is required.";
    }

    if (current === "individual_kyc") {
      if (!individual.firstName || !individual.lastName)
        return "Please enter your first and last name.";
      if (!individual.dob) return "Date of birth is required.";
      if (!individual.idNumber && !individual.nin && !individual.bvn) {
        return "Please provide at least one identifier (ID Number / NIN / BVN).";
      }
    }

    if (current === "business_kyc") {
      if (!business.legalName) return "Business legal name is required.";
      if (!business.industry) return "Please select an industry.";
      if (!business.businessType) return "Business type is required.";
      if (!business.rcNumber && !business.tin) {
        return "Please provide at least one business identifier (CAC RC Number or TIN).";
      }
      if (!business.directorName)
        return "Director/Owner full name is required.";
    }

    if (current === "prefs") {
      if (!preferences.currency) return "Currency is required.";
      if (!preferences.invoicePrefix) return "Invoice prefix is required.";
    }

    if (current === "review") {
      if (!consent.privacy || !consent.kyc || !consent.truthful) {
        return "Please accept the required declarations to continue.";
      }
    }

    return "";
  }, [
    current,
    accountType,
    contact,
    individual,
    business,
    preferences,
    consent,
  ]);

  const next = () => {
    const err = validateStep();
    if (err) return setPageErr(err);
    if (canGoNext) setStep((s) => s + 1);
  };

  const back = () => {
    setPageErr("");
    if (canGoBack) setStep((s) => s - 1);
  };

  // ✅ Load: user + company onboarding + profile + (best-effort) company_info prefill
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setPageErr("");

      try {
        const {
          data: { user: authUser },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !authUser) {
          if (!cancelled) navigate("/login", { replace: true });
          return;
        }
        if (cancelled) return;
        setUser(authUser);

        // Company-wide onboarding check
        if (companyId) {
          const { completed } = await getCompanyOnboarding(companyId);
          if (cancelled) return;

          if (completed) {
            // Best-effort: mark profile onboarded too
            try {
              await supabase
                .from("profiles")
                .upsert([{ id: authUser.id, has_onboarded: true }]);
            } catch {}
            navigate("/dashboard", { replace: true });
            return;
          }
        }

        // Profile load
        const { data: prof, error: pErr } = await supabase
          .from("profiles")
          .select(
            "id, has_onboarded, business_name, industry, business_type, country, currency, invoice_prefix, has_seen_walkthrough",
          )
          .eq("id", authUser.id)
          .maybeSingle();

        if (pErr) console.error(pErr);
        if (cancelled) return;

        setProfile(prof || null);

        if (prof?.has_onboarded) {
          // Let through even if company_onboarding isn't set
          if (companyId) {
            try {
              await supabase.from("company_onboarding").upsert(
                [
                  {
                    company_id: companyId,
                    completed: true,
                    completed_at: new Date().toISOString(),
                    completed_by: authUser.id,
                    updated_at: new Date().toISOString(),
                  },
                ],
                { onConflict: "company_id" },
              );
            } catch {}
          }
          navigate("/dashboard", { replace: true });
          return;
        }

        // Prefill contact email from auth
        setContact((c) => ({ ...c, email: authUser.email || c.email }));

        // ✅ Best-effort: prefill from company_info if exists
        if (companyId) {
          const { data: ci, error: ciErr } = await supabase
            .from("company_info")
            .select("*")
            .eq("company_id", companyId)
            .maybeSingle();

          // If table missing or RLS blocks, don't break onboarding
          if (!ciErr && ci) {
            if (ci.account_type)
              setAccountType(String(ci.account_type).toLowerCase());

            setContact((c) => ({
              ...c,
              email: ci.contact_email || c.email,
              phone: ci.contact_phone || c.phone,
              country: ci.country || c.country,
              state: ci.state || c.state,
              address: ci.address || c.address,
            }));

            setPreferences((p) => ({
              ...p,
              currency: ci.currency || p.currency,
              invoicePrefix: ci.invoice_prefix || p.invoicePrefix,
              paymentMethods:
                Array.isArray(ci.payment_methods) && ci.payment_methods.length
                  ? ci.payment_methods
                  : p.paymentMethods,
              features:
                Array.isArray(ci.features) && ci.features.length
                  ? ci.features
                  : p.features,
              taxSystem: ci.tax_system || p.taxSystem,
              vatRegistered:
                typeof ci.vat_registered === "boolean"
                  ? ci.vat_registered
                  : p.vatRegistered,
              vatRate:
                ci.vat_rate !== null && ci.vat_rate !== undefined
                  ? Number(ci.vat_rate)
                  : p.vatRate,
            }));

            setIndividual((i) => ({
              ...i,
              firstName: ci.individual_first_name || i.firstName,
              lastName: ci.individual_last_name || i.lastName,
              dob: ci.individual_dob || i.dob,
              idType: ci.individual_id_type || i.idType,
              idNumber: ci.individual_id_number || i.idNumber,
              bvn: ci.individual_bvn || i.bvn,
              nin: ci.individual_nin || i.nin,
              nextOfKinName: ci.next_of_kin_name || i.nextOfKinName,
              nextOfKinPhone: ci.next_of_kin_phone || i.nextOfKinPhone,
            }));

            setBusiness((b) => ({
              ...b,
              legalName: ci.legal_name || b.legalName,
              tradingName: ci.trading_name || b.tradingName,
              industry: ci.industry || b.industry,
              businessType: ci.business_type || b.businessType,
              rcNumber: ci.rc_number || b.rcNumber,
              tin: ci.tin || b.tin,
              incorporationDate: ci.incorporation_date || b.incorporationDate,
              website: ci.website || b.website,
              directorName: ci.director_name || b.directorName,
              directorIdType: ci.director_id_type || b.directorIdType,
              directorIdNumber: ci.director_id_number || b.directorIdNumber,
            }));

            setConsent((x) => ({
              ...x,
              privacy: !!ci.consent_privacy || x.privacy,
              kyc: !!ci.consent_kyc || x.kyc,
              truthful: !!ci.consent_truthful || x.truthful,
            }));
          }
        }

        // Walkthrough once
        const hasSeen = !!prof?.has_seen_walkthrough;
        if (!hasSeen) setHelpOpen(true);
      } catch (e) {
        console.error("Onboarding load error:", e);
        if (!cancelled)
          setPageErr("Failed to load onboarding. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, companyId]);

  const markWalkthroughSeen = useCallback(async () => {
    if (!user?.id) return;
    try {
      await supabase
        .from("profiles")
        .update({ has_seen_walkthrough: true })
        .eq("id", user.id);
    } catch {}
  }, [user?.id]);

  const closeHelp = async () => {
    setHelpOpen(false);
    await markWalkthroughSeen();
  };

  const submit = async () => {
    const err = validateStep();
    if (err) return setPageErr(err);

    if (!user?.id) return;
    if (!companyId) {
      setPageErr("Company ID missing. Reload dashboard or re-login.");
      return;
    }

    setSaving(true);
    setPageErr("");

    try {
      // 1) Update profiles (backward compatible)
      const businessName =
        accountType === "business"
          ? business.legalName
          : `${individual.firstName} ${individual.lastName}`.trim();

      const industry =
        accountType === "business"
          ? business.industry
          : "Individual/Freelancer";
      const businessType =
        accountType === "business" ? business.businessType : "Individual";

      const profilePayload = {
        id: user.id,
        has_onboarded: true,
        country: contact.country,
        currency: preferences.currency,
        invoice_prefix: preferences.invoicePrefix,
        business_name: businessName,
        industry,
        business_type: businessType,
      };

      const { error: profErr } = await supabase
        .from("profiles")
        .upsert([profilePayload]);
      if (profErr) throw profErr;

      // 2) Company-wide onboarding (best effort)
      const { error: coErr } = await supabase.from("company_onboarding").upsert(
        [
          {
            company_id: companyId,
            completed: true,
            completed_at: new Date().toISOString(),
            completed_by: user.id,
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: "company_id" },
      );
      if (coErr) console.warn("company_onboarding upsert failed:", coErr);

      // 3) ✅ Save ALL onboarding info to public.company_info (best effort)
      const onboardingPayload = compact({
        company_id: companyId,
        account_type: accountType,
        contact: compact(contact),
        preferences: compact(preferences),
        individual: accountType === "individual" ? compact(individual) : null,
        business: accountType === "business" ? compact(business) : null,
        consent: compact(consent),
        saved_at: new Date().toISOString(),
      });

      const companyInfoRow = {
        company_id: companyId,
        account_type: accountType || null,

        // contact
        contact_email: contact.email || null,
        email: contact.email || null,
        contact_phone: contact.phone || null,
        phone_number: contact.phone || null,
        country: contact.country || null,
        state: contact.state || null,
        address: contact.address || null,

        // preferences
        currency: preferences.currency || null,
        invoice_prefix: preferences.invoicePrefix || null,
        payment_methods: preferences.paymentMethods || [],
        features: preferences.features || [],
        tax_system: preferences.taxSystem || null,
        vat_registered: !!preferences.vatRegistered,
        vat_rate: Number(preferences.vatRate || 0),

        // individual
        individual_first_name: individual.firstName || null,
        individual_last_name: individual.lastName || null,
        individual_dob: individual.dob || null,
        individual_id_type: individual.idType || null,
        individual_id_number: individual.idNumber || null,
        individual_bvn: individual.bvn || null,
        individual_nin: individual.nin || null,
        next_of_kin_name: individual.nextOfKinName || null,
        next_of_kin_phone: individual.nextOfKinPhone || null,

        // business
        legal_name: business.legalName || null,
        company_name: business.legalName || null,
        trading_name: business.tradingName || null,
        industry: business.industry || null,
        business_type: business.businessType || null,
        rc_number: business.rcNumber || null,
        tin: business.tin || null,
        TIN: business.tin || null,
        incorporation_date: business.incorporationDate || null,
        website: business.website || null,
        director_name: business.directorName || null,
        director_id_type: business.directorIdType || null,
        director_id_number: business.directorIdNumber || null,

        // consent
        consent_privacy: !!consent.privacy,
        consent_kyc: !!consent.kyc,
        consent_truthful: !!consent.truthful,

        onboarding_payload: onboardingPayload,
        updated_at: new Date().toISOString(),
      };

      const { error: ciErr } = await supabase
        .from("company_info")
        .upsert([companyInfoRow], { onConflict: "company_id" });

      if (ciErr) console.warn("company_info save failed:", ciErr);

      // 4) existing KYC table save (kept)
      const kycPayload = compact({
        company_id: companyId || null,
        account_type: accountType,
        contact: compact(contact),
        preferences: compact(preferences),
        individual: accountType === "individual" ? compact(individual) : null,
        business: accountType === "business" ? compact(business) : null,
      });

      const { error: kycErr } = await supabase.from("onboarding_kyc").upsert(
        [
          {
            user_id: user.id,
            company_id: companyId || null,
            account_type: accountType,
            kyc_payload: kycPayload,
            consent: true,
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: "user_id,company_id" },
      );

      if (kycErr) console.warn("KYC save error:", kycErr);

      navigate("/dashboard", { replace: true });
    } catch (e) {
      console.error(e);
      setPageErr("Failed to complete onboarding. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const headerText = useMemo(() => {
    if (current === "welcome") return "Welcome to KostPadi";
    if (current === "type") return "Tell us who you are";
    if (current === "contact") return "Contact & Address";
    if (current === "individual_kyc") return "Personal KYC (Nigeria-ready)";
    if (current === "business_kyc") return "Business KYC (Nigeria-ready)";
    if (current === "prefs") return "Preferences";
    if (current === "review") return "Review & Finish";
    return "Onboarding";
  }, [current]);

  const subtitleText = useMemo(() => {
    if (current === "welcome")
      return "A quick setup so your invoices, journals, and reports are accurate from day one.";
    if (current === "type")
      return "We tailor onboarding fields depending on whether you're an individual/freelancer or a registered business.";
    if (current === "contact")
      return "We use this for invoice headers, compliance, and account security.";
    if (current === "individual_kyc")
      return "Provide basic identity details. You can complete advanced verification later if needed.";
    if (current === "business_kyc")
      return "Provide core business identifiers (CAC/TIN). You can update this anytime in Settings.";
    if (current === "prefs")
      return "Set currency, invoice numbering, payment methods, and features you want visible.";
    if (current === "review")
      return "Confirm details and accept declarations to complete onboarding.";
    return "";
  }, [current]);

  if (loading) {
    return (
      <div className="kp-onb-shell">
        <div className="kp-onb-card">
          <div className="kp-loading">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="kp-onb-shell">
      <WalkthroughModal open={helpOpen} onClose={closeHelp} />

      <button
        className="kp-help-fab"
        type="button"
        onClick={() => setHelpOpen(true)}
        title="Help / Walkthrough"
      >
        <HelpCircle size={18} />
        <span>Help</span>
      </button>

      <div className="kp-onb-card">
        <header className="kp-onb-header">
          <div>
            <h2>{headerText}</h2>
            <p className="kp-muted">{subtitleText}</p>
          </div>

          <div className="kp-progress">
            <div className="kp-steps">
              {steps.map((s, idx) => (
                <div
                  key={s.key}
                  className={`kp-step ${idx === step ? "active" : ""} ${
                    idx < step ? "done" : ""
                  }`}
                  title={s.title}
                >
                  <span className="kp-step-ic">{s.icon}</span>
                  <span className="kp-step-txt">{s.title}</span>
                </div>
              ))}
            </div>
            <div className="kp-bar">
              <div
                className="kp-bar-fill"
                style={{
                  width: `${Math.round(((step + 1) / steps.length) * 100)}%`,
                }}
              />
            </div>
          </div>
        </header>

        {pageErr && <div className="kp-alert">{pageErr}</div>}

        <main className="kp-onb-body">
          {/* Step: Welcome */}
          {current === "welcome" && (
            <div className="kp-pane">
              <div className="kp-welcome">
                <div className="kp-welcome-ic">
                  <BadgeCheck size={22} />
                </div>
                <div>
                  <h3>Let’s set up your workspace</h3>
                  <p className="kp-muted">
                    This onboarding is designed for Nigerian users and SMEs, and
                    also works well for freelancers. You can edit these details
                    later in Settings.
                  </p>

                  <div className="kp-callouts">
                    <div className="kp-callout">
                      <ShieldCheck size={16} />
                      <div>
                        <strong>Accurate accounting</strong>
                        <span>
                          Journal-driven reporting across TB, GL, IS, SOFP,
                          Cashflows.
                        </span>
                      </div>
                    </div>
                    <div className="kp-callout">
                      <FileText size={16} />
                      <div>
                        <strong>Professional invoices</strong>
                        <span>
                          Brand colors, clean layouts, and export-ready PDFs.
                        </span>
                      </div>
                    </div>
                    <div className="kp-callout">
                      <HelpCircle size={16} />
                      <div>
                        <strong>Guided walkthrough</strong>
                        <span>
                          Watch feature videos anytime from the Help button.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="kp-row">
                    <button
                      className="kp-btn kp-btn-ghost"
                      type="button"
                      onClick={() => setHelpOpen(true)}
                    >
                      <HelpCircle size={16} /> Watch walkthrough
                    </button>
                    <button
                      className="kp-btn kp-btn-primary"
                      type="button"
                      onClick={next}
                    >
                      Get Started <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step: Account Type */}
          {current === "type" && (
            <div className="kp-pane">
              <div className="kp-grid2">
                <button
                  type="button"
                  className={`kp-choice ${
                    accountType === "individual" ? "active" : ""
                  }`}
                  onClick={() => setAccountType("individual")}
                >
                  <div className="kp-choice-ic">
                    <User2 size={18} />
                  </div>
                  <div className="kp-choice-txt">
                    <strong>Individual / Freelancer</strong>
                    <span>Personal invoices, basic KYC, simple setup</span>
                  </div>
                </button>

                <button
                  type="button"
                  className={`kp-choice ${
                    accountType === "business" ? "active" : ""
                  }`}
                  onClick={() => setAccountType("business")}
                >
                  <div className="kp-choice-ic">
                    <Building2 size={18} />
                  </div>
                  <div className="kp-choice-txt">
                    <strong>Business</strong>
                    <span>
                      CAC/TIN identifiers, directors/owners, business reporting
                    </span>
                  </div>
                </button>
              </div>

              <div className="kp-note">
                We only request what’s needed to set up documents and keep your
                account secure. Advanced verification can be added later if
                required.
              </div>
            </div>
          )}

          {/* Step: Contact */}
          {current === "contact" && (
            <div className="kp-pane">
              <div className="kp-form">
                <div className="kp-field">
                  <label>Email</label>
                  <input
                    value={contact.email}
                    onChange={(e) =>
                      setContact((c) => ({ ...c, email: e.target.value }))
                    }
                    placeholder="name@domain.com"
                  />
                </div>

                <div className="kp-field">
                  <label>Phone number</label>
                  <input
                    value={contact.phone}
                    onChange={(e) =>
                      setContact((c) => ({ ...c, phone: e.target.value }))
                    }
                    placeholder="080…"
                  />
                </div>

                <div className="kp-field">
                  <label>Country</label>
                  <input
                    value={contact.country}
                    onChange={(e) =>
                      setContact((c) => ({ ...c, country: e.target.value }))
                    }
                    placeholder="Nigeria"
                  />
                </div>

                <div className="kp-field">
                  <label>State</label>
                  <input
                    value={contact.state}
                    onChange={(e) =>
                      setContact((c) => ({ ...c, state: e.target.value }))
                    }
                    placeholder="Lagos"
                  />
                </div>

                <div className="kp-field kp-span2">
                  <label>Address</label>
                  <textarea
                    value={contact.address}
                    onChange={(e) =>
                      setContact((c) => ({ ...c, address: e.target.value }))
                    }
                    placeholder="Full address (used on invoices)"
                  />
                </div>
              </div>

              <div className="kp-mini">
                Tip: Keep address consistent with your invoices and receipts for
                better professionalism.
              </div>
            </div>
          )}

          {/* Step: Individual KYC */}
          {current === "individual_kyc" && (
            <div className="kp-pane">
              <div className="kp-form">
                <div className="kp-field">
                  <label>First name</label>
                  <input
                    value={individual.firstName}
                    onChange={(e) =>
                      setIndividual((p) => ({
                        ...p,
                        firstName: e.target.value,
                      }))
                    }
                    placeholder="Oluwatobi"
                  />
                </div>

                <div className="kp-field">
                  <label>Last name</label>
                  <input
                    value={individual.lastName}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, lastName: e.target.value }))
                    }
                    placeholder="Adeyemi"
                  />
                </div>

                <div className="kp-field">
                  <label>Date of birth</label>
                  <input
                    type="date"
                    value={individual.dob}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, dob: e.target.value }))
                    }
                    max={monthISO()}
                  />
                </div>

                <div className="kp-field">
                  <label>ID type</label>
                  <select
                    value={individual.idType}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, idType: e.target.value }))
                    }
                  >
                    {ID_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="kp-field kp-span2">
                  <label>ID number</label>
                  <input
                    value={individual.idNumber}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, idNumber: e.target.value }))
                    }
                    placeholder="Enter your ID number"
                  />
                </div>

                <div className="kp-field">
                  <label>NIN (optional)</label>
                  <input
                    value={individual.nin}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, nin: e.target.value }))
                    }
                    placeholder="Optional"
                  />
                </div>

                <div className="kp-field">
                  <label>BVN (optional)</label>
                  <input
                    value={individual.bvn}
                    onChange={(e) =>
                      setIndividual((p) => ({ ...p, bvn: e.target.value }))
                    }
                    placeholder="Optional"
                  />
                </div>

                <div className="kp-field kp-span2">
                  <label>Next of kin (optional)</label>
                  <div className="kp-inline2">
                    <input
                      value={individual.nextOfKinName}
                      onChange={(e) =>
                        setIndividual((p) => ({
                          ...p,
                          nextOfKinName: e.target.value,
                        }))
                      }
                      placeholder="Name"
                    />
                    <input
                      value={individual.nextOfKinPhone}
                      onChange={(e) =>
                        setIndividual((p) => ({
                          ...p,
                          nextOfKinPhone: e.target.value,
                        }))
                      }
                      placeholder="Phone"
                    />
                  </div>
                </div>
              </div>

              <div className="kp-note">
                <strong>Privacy note:</strong> Only request what you need. Use
                clear consent and avoid storing sensitive identifiers if not
                necessary for your product’s purpose.
              </div>
            </div>
          )}

          {/* Step: Business KYC */}
          {current === "business_kyc" && (
            <div className="kp-pane">
              <div className="kp-form">
                <div className="kp-field kp-span2">
                  <label>Business legal name</label>
                  <input
                    value={business.legalName}
                    onChange={(e) =>
                      setBusiness((b) => ({ ...b, legalName: e.target.value }))
                    }
                    placeholder="Registered name (CAC)"
                  />
                </div>

                <div className="kp-field kp-span2">
                  <label>Trading name (optional)</label>
                  <input
                    value={business.tradingName}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        tradingName: e.target.value,
                      }))
                    }
                    placeholder="Brand name if different"
                  />
                </div>

                <div className="kp-field">
                  <label>Industry</label>
                  <select
                    value={business.industry}
                    onChange={(e) =>
                      setBusiness((b) => ({ ...b, industry: e.target.value }))
                    }
                  >
                    <option value="">Select</option>
                    {INDUSTRIES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="kp-field">
                  <label>Business type</label>
                  <select
                    value={business.businessType}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        businessType: e.target.value,
                      }))
                    }
                  >
                    {BUSINESS_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="kp-field">
                  <label>CAC RC Number (optional)</label>
                  <input
                    value={business.rcNumber}
                    onChange={(e) =>
                      setBusiness((b) => ({ ...b, rcNumber: e.target.value }))
                    }
                    placeholder="RC…"
                  />
                </div>

                <div className="kp-field">
                  <label>TIN (optional)</label>
                  <input
                    value={business.tin}
                    onChange={(e) =>
                      setBusiness((b) => ({ ...b, tin: e.target.value }))
                    }
                    placeholder="Tax Identification Number"
                  />
                </div>

                <div className="kp-field">
                  <label>Incorporation date (optional)</label>
                  <input
                    type="date"
                    value={business.incorporationDate}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        incorporationDate: e.target.value,
                      }))
                    }
                    max={monthISO()}
                  />
                </div>

                <div className="kp-field">
                  <label>Website (optional)</label>
                  <input
                    value={business.website}
                    onChange={(e) =>
                      setBusiness((b) => ({ ...b, website: e.target.value }))
                    }
                    placeholder="https://…"
                  />
                </div>

                <div className="kp-divider" />

                <div className="kp-field kp-span2">
                  <label>Director/Owner full name</label>
                  <input
                    value={business.directorName}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        directorName: e.target.value,
                      }))
                    }
                    placeholder="Full name"
                  />
                </div>

                <div className="kp-field">
                  <label>Director ID type</label>
                  <select
                    value={business.directorIdType}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        directorIdType: e.target.value,
                      }))
                    }
                  >
                    {ID_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="kp-field">
                  <label>Director ID number</label>
                  <input
                    value={business.directorIdNumber}
                    onChange={(e) =>
                      setBusiness((b) => ({
                        ...b,
                        directorIdNumber: e.target.value,
                      }))
                    }
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="kp-mini">
                Tip: If you’re a registered business, adding CAC/TIN improves
                invoice trust and audit readiness.
              </div>
            </div>
          )}

          {/* Step: Preferences */}
          {current === "prefs" && (
            <div className="kp-pane">
              <div className="kp-form">
                <div className="kp-field">
                  <label>Currency</label>
                  <select
                    value={preferences.currency}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        currency: e.target.value,
                      }))
                    }
                  >
                    <option value="NGN">NGN (₦)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="EUR">EUR (€)</option>
                  </select>
                </div>

                <div className="kp-field">
                  <label>Invoice prefix</label>
                  <input
                    value={preferences.invoicePrefix}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        invoicePrefix: e.target.value,
                      }))
                    }
                    placeholder="INV-"
                  />
                </div>

                <div className="kp-field kp-span2">
                  <label>Payment methods (shown on invoice / notes)</label>
                  <div className="kp-chips">
                    {PAYMENT_METHODS.map((x) => (
                      <button
                        key={x}
                        type="button"
                        className={`kp-chip ${
                          preferences.paymentMethods.includes(x) ? "on" : ""
                        }`}
                        onClick={() => setPaymentToggle(x)}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="kp-field kp-span2">
                  <label>Core features you want enabled</label>
                  <div className="kp-chips">
                    {FEATURE_OPTIONS.map((x) => (
                      <button
                        key={x}
                        type="button"
                        className={`kp-chip ${
                          preferences.features.includes(x) ? "on" : ""
                        }`}
                        onClick={() => setFeatureToggle(x)}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="kp-field">
                  <label className="kp-check">
                    <input
                      type="checkbox"
                      checked={preferences.vatRegistered}
                      onChange={(e) =>
                        setPreferences((p) => ({
                          ...p,
                          vatRegistered: e.target.checked,
                        }))
                      }
                    />
                    <span>VAT registered</span>
                  </label>
                </div>

                <div className="kp-field">
                  <label>VAT rate</label>
                  <input
                    type="number"
                    step="0.001"
                    value={preferences.vatRate}
                    onChange={(e) =>
                      setPreferences((p) => ({
                        ...p,
                        vatRate: Number(e.target.value || 0),
                      }))
                    }
                  />
                  <small className="kp-muted">
                    Default 7.5% for Nigeria. Adjust if needed.
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Step: Review */}
          {current === "review" && (
            <div className="kp-pane">
              <div className="kp-review">
                <div className="kp-review-card">
                  <h4>Summary</h4>
                  <div className="kp-kv">
                    <div>
                      <span>Account type</span>
                      <strong>{accountType || "—"}</strong>
                    </div>
                    <div>
                      <span>Email</span>
                      <strong>{contact.email || "—"}</strong>
                    </div>
                    <div>
                      <span>Phone</span>
                      <strong>{contact.phone || "—"}</strong>
                    </div>
                    <div>
                      <span>Currency</span>
                      <strong>{preferences.currency || "—"}</strong>
                    </div>
                    <div className="kp-span2">
                      <span>Address</span>
                      <strong>{contact.address || "—"}</strong>
                    </div>
                    <div className="kp-span2">
                      <span>Features</span>
                      <strong>{preferences.features.join(", ") || "—"}</strong>
                    </div>
                  </div>
                </div>

                <div className="kp-review-card">
                  <h4>Declarations</h4>

                  <label className="kp-check kp-stack">
                    <input
                      type="checkbox"
                      checked={consent.privacy}
                      onChange={(e) =>
                        setConsent((c) => ({ ...c, privacy: e.target.checked }))
                      }
                    />
                    <span>
                      I consent to KostPadi processing my data for onboarding
                      and account operations (as described in the Privacy
                      Notice).
                    </span>
                  </label>

                  <label className="kp-check kp-stack">
                    <input
                      type="checkbox"
                      checked={consent.kyc}
                      onChange={(e) =>
                        setConsent((c) => ({ ...c, kyc: e.target.checked }))
                      }
                    />
                    <span>
                      I understand that KYC details may be required for
                      compliance and fraud prevention and can be requested again
                      later.
                    </span>
                  </label>

                  <label className="kp-check kp-stack">
                    <input
                      type="checkbox"
                      checked={consent.truthful}
                      onChange={(e) =>
                        setConsent((c) => ({
                          ...c,
                          truthful: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      I confirm that the information provided is accurate to the
                      best of my knowledge.
                    </span>
                  </label>

                  <div className="kp-mini">
                    If you’re collecting sensitive identifiers (like BVN/NIN),
                    ensure you have strong access control, minimal collection,
                    and clear user consent.
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        <footer className="kp-onb-footer">
          <button
            className="kp-btn kp-btn-ghost"
            type="button"
            onClick={back}
            disabled={!canGoBack || saving}
          >
            <ChevronLeft size={16} /> Back
          </button>

          <div className="kp-footer-right">
            {current !== "review" ? (
              <button
                className="kp-btn kp-btn-primary"
                type="button"
                onClick={next}
                disabled={saving}
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                className="kp-btn kp-btn-primary"
                type="button"
                onClick={submit}
                disabled={saving}
              >
                {saving ? "Finishing…" : "Finish & Go to Dashboard"}{" "}
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
